import { useState, useEffect, useCallback, useRef } from "react";
import { trustedAgentService } from "../../services/clubNetworkApi";

// Club-side list of own Trusted-Agent search requests (design N3·4 + A15). Close
// and withdraw are soft transitions (no delete) — the goldene Regel forbids
// destructive ops. `bucket` drives the A15 Active/Archive tab (?bucket=), and
// the response carries the active/archive counts for the tab pills.
export const useSearchRequests = (bucket = null) => {
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({ active: null, archive: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const res = await trustedAgentService.listRequests({ bucket }, controller.signal);
      if (res.success) {
        setRequests(res.data);
        setCounts({ active: res.activeCount, archive: res.archiveCount });
      } else setError(res.error || "Failed to load your search requests.");
    } catch (err) {
      if (err?.name === "CanceledError" || err?.name === "AbortError") return;
      setError("Failed to load your search requests. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [bucket]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const closeRequest = useCallback(async (id) => {
    const res = await trustedAgentService.closeRequest(id);
    if (res.success) {
      // The lifecycle bucket changes on close — refetch so the request re-buckets
      // to Archive and the tab counts stay honest.
      fetchRequests();
    }
    return res;
  }, [fetchRequests]);

  const withdrawRequest = useCallback(async (id) => {
    const res = await trustedAgentService.withdrawRequest(id);
    if (res.success) fetchRequests();
    return res;
  }, [fetchRequests]);

  return {
    requests,
    counts,
    loading,
    error,
    fetchRequests,
    closeRequest,
    withdrawRequest,
  };
};
